/**
 * Error types and interfaces for API error handling
 */

// API error response structure (matches backend ErrorResponse)
export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: string;
  field?: string;
  metadata?: Record<string, any>;
  timestamp: string;
  /**
   * A message the backend has explicitly marked safe to show verbatim, even for 401/403 —
   * whose bodies are otherwise masked because the role-check middleware puts customer UUIDs
   * and required-role lists in them. Only ever set deliberately by a handler that authored a
   * user-appropriate sentence; never by generic middleware. See parseApiError.
   */
  user_message?: string;
}

// Error type classification
export enum ErrorType {
  SERVER_ERROR = 'server_error',
  NETWORK_ERROR = 'network_error',
  AUTH_ERROR = 'auth_error',
  PERMISSION_DENIED = 'permission_denied',
  VALIDATION_ERROR = 'validation_error',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  PRECONDITION_FAILED = 'precondition_failed',
  UNKNOWN = 'unknown'
}

// Error domain classification
export enum ErrorDomain {
  AUTH = 'AUTH',
  PERSON = 'PERSON',
  DEVICE = 'DEVICE',
  ZONE = 'ZONE',
  ACCESS = 'ACCESS',
  FILE = 'FILE',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN'
}

// Parsed error with classification
export interface ParsedError {
  type: ErrorType;
  domain: ErrorDomain;
  code: string;
  message: string;
  field?: string;
  details?: string;
  httpStatus: number;
  isRetryable: boolean;
}

// Legacy error format (for backward compatibility)
export interface LegacyErrorResponse {
  error: string;
  message?: string;
  /** See ApiErrorResponse.user_message — opt-in passthrough for masked statuses. */
  user_message?: string;
}
